/**
 * OccupationalExam Service
 * API integration for occupational exam catalog CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API do catálogo de exames ocupacionais
 */
import apiClient from './apiClient';
import {
  OccupationalExam,
  OccupationalExamFilters,
  OccupationalExamFormData,
  OccupationalExamListResponse,
  OccupationalExamDependencies,
} from '../types/occupationalExam';

// Re-export types for convenience
export * from '../types/occupationalExam';

export interface OccupationalExamListParams extends OccupationalExamFilters {
  page?: number | undefined;
  limit?: number | undefined;
}

class OccupationalExamService {
  private baseUrl = '/occupational-exams';

  /**
   * Get paginated list of occupational exams
   */
  async getOccupationalExams(params: OccupationalExamListParams = {}): Promise<OccupationalExamListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.examType) queryParams.append('examType', filters.examType);
    if (filters.workType) queryParams.append('workType', filters.workType);
    if (filters.includeESocial !== undefined) queryParams.append('includeESocial', String(filters.includeESocial));
    if (filters.includePPP !== undefined) queryParams.append('includePPP', String(filters.includePPP));
    if (filters.includeASO !== undefined) queryParams.append('includeASO', String(filters.includeASO));
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { data: { exams, total, page, limit, totalPages } }
    const backendData = response.data.data || response.data;

    return {
      exams: backendData.exams ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  /**
   * Get occupational exam by ID
   */
  async getOccupationalExamById(id: string | number): Promise<OccupationalExam> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new occupational exam
   */
  async createOccupationalExam(data: OccupationalExamFormData): Promise<OccupationalExam> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  /**
   * Update occupational exam by ID
   */
  async updateOccupationalExam(id: string | number, data: Partial<OccupationalExamFormData>): Promise<OccupationalExam> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete occupational exam by ID (soft delete)
   */
  async deleteOccupationalExam(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate occupational exam
   */
  async activateOccupationalExam(id: string | number): Promise<OccupationalExam> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/activate`);
    return response.data.data || response.data;
  }

  /**
   * Deactivate occupational exam
   */
  async deactivateOccupationalExam(id: string | number): Promise<OccupationalExam> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/deactivate`);
    return response.data.data || response.data;
  }

  /**
   * Get exams by exam type (preEmployment, periodic, dismissal, etc.)
   */
  async getByExamType(
    examType: 'preEmployment' | 'periodic' | 'dismissal' | 'complementary' | 'returnToWork' | 'riskChange' | 'clinicalConsultation',
  ): Promise<OccupationalExam[]> {
    const url = `${this.baseUrl}/type/${examType}`;
    const response = await apiClient.get(url);
    return response.data.data || response.data;
  }

  /**
   * Check dependencies before deletion
   */
  async checkDependencies(id: string | number): Promise<OccupationalExamDependencies> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  /**
   * Get exam types summary (helper for UI)
   */
  getExamTypesSummary(exam: OccupationalExam): string[] {
    const types: string[] = [];
    if (exam.preEmployment) types.push('Admissional');
    if (exam.periodic) types.push('Periódico');
    if (exam.dismissal) types.push('Demissional');
    if (exam.complementary) types.push('Complementar');
    if (exam.returnToWork) types.push('Retorno ao Trabalho');
    if (exam.riskChange) types.push('Mudança de Risco');
    if (exam.clinicalConsultation) types.push('Consulta Clínica');
    return types;
  }

  /**
   * Get work types summary (helper for UI)
   */
  getWorkTypesSummary(exam: OccupationalExam): string[] {
    const types: string[] = [];
    if (exam.foodIndustry) types.push('Indústria Alimentícia');
    if (exam.electrical) types.push('Eletricidade (NR-10)');
    if (exam.pta) types.push('PTA');
    if (exam.confinedSpace) types.push('Espaço Confinado (NR-33)');
    if (exam.heightWork) types.push('Trabalho em Altura (NR-35)');
    if (exam.forklift) types.push('Empilhadeira');
    if (exam.selfPropelledMachine) types.push('Máquina Autopropelida');
    if (exam.hotWork) types.push('Trabalho a Quente');
    return types;
  }

  /**
   * Get report types summary (helper for UI)
   */
  getReportTypesSummary(exam: OccupationalExam): string[] {
    const types: string[] = [];
    if (exam.eSocial) types.push('eSocial (S-2220)');
    if (exam.PPP) types.push('PPP');
    if (exam.ASO) types.push('ASO');
    if (exam.PPR) types.push('PPR');
    if (exam.PCMSO) types.push('PCMSO');
    if (exam.analyticalReport) types.push('Relatório Analítico');
    if (exam.PCA) types.push('PCA');
    return types;
  }

  /**
   * Validate TUSS code format
   * Pattern: XX.XX.XX.XXX-X (e.g., 40.30.40.016-1)
   */
  validateTussCode(tussCode: string): boolean {
    const tussPattern = /^\d{2}\.\d{2}\.\d{2}\.\d{3}-\d$/;
    return tussPattern.test(tussCode);
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }
}

// Export singleton instance
export const occupationalExamService = new OccupationalExamService();
export default occupationalExamService;
